import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from './Footer';
import LegalModal from './LegalModal';
import { useTheme } from '../context/ThemeContext';
import './Settings.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ─── ICONS ───

function ArrowLeftIcon({ size = 20, color = '#434F4D' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function LockIcon({ size = 20, color = '#434F4D' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function DocumentIcon({ size = 20, color = '#434F4D' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="15" y2="17" />
    </svg>
  );
}

function KeyIcon({ size = 20, color = '#434F4D' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7.5" cy="15.5" r="3.5" />
      <line x1="10" y1="13" x2="20" y2="3" />
      <line x1="16" y1="7" x2="20" y2="11" />
    </svg>
  );
}

function TrashIcon({ size = 20, color = '#DC4545' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function ChevronRightIcon({ size = 18, color = '#A0ABA8' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function CloseIcon({ size = 18, color = '#434F4D' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SunIcon({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
      <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
      <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
    </svg>
  );
}

function MoonIcon({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function MonitorIcon({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function PaletteIcon({ size = 20, color = '#434F4D' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r="0.8" fill={color} />
      <circle cx="17.5" cy="10.5" r="0.8" fill={color} />
      <circle cx="8.5" cy="7.5" r="0.8" fill={color} />
      <circle cx="6.5" cy="12.5" r="0.8" fill={color} />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.62-.38-1 0-.83.67-1.5 1.5-1.5H16c3.31 0 6-2.69 6-6 0-4.96-4.49-9-10-9z" />
    </svg>
  );
}

function SpinnerIcon({ size = 16, color = '#5CB89A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="settings-spinner">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="3" strokeOpacity="0.25" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ─── BACKEND HELPERS ───

function getAuthData() {
  try {
    const rawToken = localStorage.getItem('accessToken');
    let token = rawToken;
    if (rawToken && rawToken.startsWith('{')) {
      try {
        const parsed = JSON.parse(rawToken);
        token = parsed.accessToken || parsed.access_token || parsed.token || rawToken;
      } catch {}
    }
    const userId = localStorage.getItem('userId');
    if (token && userId) return { token, userId };
    return null;
  } catch {
    return null;
  }
}

async function deleteUser(token, userId) {
  if (!API_BASE_URL) return false;
  try {
    const response = await fetch(`${API_BASE_URL}/user/${userId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    });
    return response.ok;
  } catch (e) {
    console.error('Error deleting user:', e);
    return false;
  }
}

// ─── SETTING ITEM ───

function SettingItem({ Icon, label, onClick, isDestructive }) {
  return (
    <button
      type="button"
      className={`settings-item ${isDestructive ? 'is-destructive' : ''}`}
      onClick={onClick}
    >
      <span className={`settings-item-icon ${isDestructive ? 'is-destructive' : ''}`}>
        <Icon size={20} color={isDestructive ? '#DC4545' : '#434F4D'} />
      </span>
      <span className="settings-item-label">{label}</span>
      <ChevronRightIcon size={18} color="#A0ABA8" />
    </button>
  );
}

// ─── CHANGE PASSWORD MODAL ───

function ChangePasswordModal({ open, onClose, onSubmit, isSaving }) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setLocalError(null);
    }
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = () => {
    setLocalError(null);
    if (!oldPassword || !newPassword) {
      setLocalError('Please fill in both password fields.');
      return;
    }
    if (newPassword.length < 6) {
      setLocalError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    onSubmit(oldPassword, newPassword);
  };

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div className="settings-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="settings-modal-header">
          <button type="button" className="settings-modal-cancel" onClick={onClose}>
            Cancel
          </button>
          <h3 className="settings-modal-title">Change Password</h3>
          <button
            type="button"
            className="settings-modal-save"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>

        <div className="settings-modal-body">
          <label className="settings-field-label">Old Password</label>
          <input
            type="password"
            className="settings-input"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            placeholder="Enter current password"
            autoComplete="current-password"
          />

          <label className="settings-field-label">New Password</label>
          <input
            type="password"
            className="settings-input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            autoComplete="new-password"
          />

          <label className="settings-field-label">Confirm New Password</label>
          <input
            type="password"
            className="settings-input"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            autoComplete="new-password"
          />

          {localError && <div className="settings-modal-error">{localError}</div>}
        </div>
      </div>
    </div>
  );
}

// ─── DELETE ACCOUNT CONFIRM MODAL ───

function ConfirmDeleteModal({ open, onClose, onConfirm, isDeleting }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div className="settings-confirm-card" onClick={(e) => e.stopPropagation()}>
        <div className="settings-confirm-icon">
          <TrashIcon size={28} color="#DC4545" />
        </div>
        <h3 className="settings-confirm-title">Delete account?</h3>
        <p className="settings-confirm-text">
          This will permanently delete your account and all your health data. This action cannot be undone.
        </p>
        <div className="settings-confirm-actions">
          <button type="button" className="settings-confirm-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="settings-confirm-btn-delete"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ───

function Settings() {
  const navigate = useNavigate();
  const { themeMode, setThemeMode } = useTheme();

  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isSavingPw, setIsSavingPw] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [legalModalType, setLegalModalType] = useState(null); // 'terms' or 'privacy'

  const [toast, setToast] = useState(null); // { type: 'success' | 'error', text }

  // Auto-clear toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const handleChangePassword = async (oldPw, newPw) => {
    // Password change not implemented yet
    setToast({ type: 'error', text: 'Password change is not available yet.' });
  };

  const handleDeleteConfirm = async () => {
    const auth = getAuthData();
    if (!auth) {
      setToast({ type: 'error', text: 'You are not logged in.' });
      setConfirmDeleteOpen(false);
      return;
    }
    setIsDeleting(true);
    const success = await deleteUser(auth.token, auth.userId);
    setIsDeleting(false);
    if (success) {
      try {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userId');
      } catch {}
      navigate('/');
    } else {
      setConfirmDeleteOpen(false);
      setToast({ type: 'error', text: 'Failed to delete account. Please try again.' });
    }
  };

  return (
    <div className="settings-page-wrapper">
      <Navbar />

      <div className="settings-container">
        {/* Toast */}
        {toast && (
          <div className={`settings-toast settings-toast--${toast.type}`}>
            {toast.text}
            <button onClick={() => setToast(null)} className="settings-toast-close" type="button">
              <CloseIcon size={14} color={toast.type === 'success' ? '#226149' : '#B93A3A'} />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="settings-page-header">
          <button
            type="button"
            className="settings-back-btn"
            onClick={() => navigate('/profile')}
            aria-label="Back"
          >
            <ArrowLeftIcon size={20} color="#434F4D" />
          </button>
          <h1 className="settings-page-title">Settings</h1>
        </div>

        {/* APPEARANCE */}
        <div className="settings-section">
          <h2 className="settings-section-title">Appearance</h2>
          <div className="settings-card settings-appearance-card">
            <div className="settings-appearance-header">
              <span className="settings-appearance-icon-wrap">
                <PaletteIcon size={20} color="currentColor" />
              </span>
              <div className="settings-appearance-meta">
                <span className="settings-appearance-label">Theme</span>
                <span className="settings-appearance-hint">
                  Choose how HRV-4 looks. System matches your device.
                </span>
              </div>
            </div>

            <div className="appearance-picker" role="radiogroup" aria-label="Theme">
              <button
                type="button"
                role="radio"
                aria-checked={themeMode === 'light'}
                className={`appearance-option ${themeMode === 'light' ? 'is-active' : ''}`}
                onClick={() => setThemeMode('light')}
              >
                <span className="appearance-option-icon">
                  <SunIcon size={22} />
                </span>
                <span className="appearance-option-label">Light</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={themeMode === 'dark'}
                className={`appearance-option ${themeMode === 'dark' ? 'is-active' : ''}`}
                onClick={() => setThemeMode('dark')}
              >
                <span className="appearance-option-icon">
                  <MoonIcon size={22} />
                </span>
                <span className="appearance-option-label">Dark</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={themeMode === 'system'}
                className={`appearance-option ${themeMode === 'system' ? 'is-active' : ''}`}
                onClick={() => setThemeMode('system')}
              >
                <span className="appearance-option-icon">
                  <MonitorIcon size={22} />
                </span>
                <span className="appearance-option-label">System</span>
              </button>
            </div>
          </div>
        </div>

        {/* SUPPORT */}
        <div className="settings-section">
          <h2 className="settings-section-title">Support</h2>
          <div className="settings-card">
            <SettingItem
              Icon={LockIcon}
              label="Privacy Policy"
              onClick={() => setLegalModalType('privacy')}
            />
            <div className="settings-separator" />
            <SettingItem
              Icon={DocumentIcon}
              label="Terms of Service"
              onClick={() => setLegalModalType('terms')}
            />
          </div>
        </div>

        {/* ACCOUNT */}
        <div className="settings-section">
          <h2 className="settings-section-title">Account</h2>
          <div className="settings-card">
            <SettingItem
              Icon={KeyIcon}
              label="Change Password"
              onClick={() => setPwModalOpen(true)}
            />
            <div className="settings-separator" />
            <SettingItem
              Icon={TrashIcon}
              label="Delete Account"
              onClick={() => setConfirmDeleteOpen(true)}
              isDestructive
            />
          </div>
        </div>

        {/* App version */}
        <p className="settings-version">App Version 1.0.0</p>
      </div>

      <Footer />

      <ChangePasswordModal
        open={pwModalOpen}
        onClose={() => setPwModalOpen(false)}
        onSubmit={handleChangePassword}
        isSaving={isSavingPw}
      />

      <ConfirmDeleteModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />

      {legalModalType && (
        <LegalModal type={legalModalType} onClose={() => setLegalModalType(null)} />
      )}
    </div>
  );
}

export default Settings;
